import React from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './organizational-structure.module.scss';
import { getLang } from 'shared/store/selectors/lang.selector';
import { TextInput } from 'src/components/design-system';
import { Modal } from 'src/components/design-system/modal';
import { getApiErrorMessage } from 'shared/utils';
import {
  useAddProfileFieldOption,
  useUpdateProfileFieldOption,
} from 'shared/hooks/profile-fields.hooks';
import { EVENTS } from 'shared/constants/analytics';
import analyticsService from 'src/helpers/analytics.service';

/**
 * Add / edit an option's label. On add for a dependent field, the parent option
 * comes from the card's current selection (`parentOptionId` / `parentLabel`) —
 * there is no in-modal parent picker. Rename (PUT) only sends `label`.
 *
 * @param {{
 *   open: boolean,
 *   field: import('shared/api/profile-fields.service.api').ProfileField,
 *   option: import('shared/api/profile-fields.service.api').ProfileFieldOption | null,
 *   parentOptionId: string | null,
 *   parentLabel: string | null,
 *   onClose: () => void,
 * }} props
 */
export function OptionModal({ open, field, option, parentOptionId, parentLabel, onClose }) {
  const lang = useSelector(getLang('SETTINGS'));
  const [api, contextHolder] = notification.useNotification();
  const addOption = useAddProfileFieldOption();
  const updateOption = useUpdateProfileFieldOption();
  const isEdit = !!option;

  const fieldLabel = field.displayLabel || field.fieldKey;
  const fieldLabelLower = fieldLabel.toLowerCase();
  const addTitle =
    parentLabel && !isEdit
      ? lang.ADD_ITEM_UNDER.replace('{label}', fieldLabelLower).replace('{parent}', parentLabel)
      : lang.ADD_ITEM.replace('{label}', fieldLabelLower);
  const title = isEdit ? lang.EDIT_ITEM.replace('{label}', fieldLabelLower) : addTitle;

  const { handleSubmit, setFieldValue, resetForm, values, errors } = useFormik({
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      label: Yup.string().trim().required(lang.FIELD_REQUIRED),
    }),
    initialValues: { label: option?.label || '' },
    onSubmit: async vals => {
      const label = vals.label.trim();
      try {
        if (isEdit) {
          await updateOption.mutateAsync({ fieldId: field.id, optionId: option._id, label });
          // Re-saving an unchanged label isn't an edit — matches how the backend
          // suppresses no-op diffs on [Knowledge] update.
          if (label !== option.label) {
            analyticsService.track(EVENTS.PROFILE_FIELDS.EDIT_OPTION, {
              group: field.group,
              fieldKey: field.fieldKey,
              optionId: option._id,
              code: option.code,
              edit_types: ['label'],
              changes: { label: { original: option.label, new: label } },
            });
          }
        } else {
          const updatedField = await addOption.mutateAsync({
            fieldId: field.id,
            label,
            parentOptionId: parentOptionId || undefined,
          });
          // The response is the whole field, so the new option's server-generated
          // _id / code have to be picked out of it.
          const created = (updatedField?.options || []).find(
            o => o.label === label && (o.parentOptionId ?? null) === (parentOptionId ?? null),
          );
          analyticsService.track(EVENTS.PROFILE_FIELDS.ADD_OPTION, {
            group: field.group,
            fieldKey: field.fieldKey,
            optionId: created?._id,
            code: created?.code,
            label,
            parentOptionId: parentOptionId ?? null,
          });
        }
        api.success({
          message: isEdit ? lang.OPTION_SAVED : lang.OPTION_CREATED,
          placement: 'bottomRight',
        });
        handleClose();
      } catch (error) {
        api.error({
          message: getApiErrorMessage(error) || lang.ORG_STRUCTURE_ERROR,
          placement: 'bottomRight',
        });
      }
    },
  });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const loading = addOption.isPending || updateOption.isPending;

  return (
    <>
      {contextHolder}
      <Modal
        title={title}
        open={open}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        cancelText={lang.CANCEL}
        submitText={isEdit ? lang.SAVE : lang.ADD_ITEM.replace('{label}', fieldLabelLower)}
        submitDisabled={!values.label.trim()}
        loading={loading}
        footerAlign="end"
        className={styles.modal}>
        <p className={styles.modalBody}>{lang.OPTION_AVAILABLE_ORG}</p>
        <TextInput
          label={lang.ITEM_NAME.replace('{label}', fieldLabel)}
          name="label"
          value={values.label}
          onChange={e => setFieldValue('label', e.target.value)}
          error={errors.label}
          placeholder={lang.OPTION_NAME_PLACEHOLDER}
          size="middle"
        />
      </Modal>
    </>
  );
}
